import { type ApolloError, useLazyQuery } from "@apollo/client";
import type { PureAbility } from "@casl/ability";
import { useCallback, useEffect } from "react";

import { groupAttributes, groupLevelPermissions } from "../config";
import { GET_GROUP_DATA } from "pages/group/queries";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";
import { translate } from "utils/translations/translate";

interface IGroupPermissionsResponse {
  error: ApolloError | undefined;
  loading: boolean;
  attributes: PureAbility<string>;
  permissions: PureAbility<string>;
  permissionMethods: {
    can: (action: string) => boolean;
    canMutate: (action: string) => boolean;
    canResolve: (action: string) => boolean;
    have: (attribute: string) => boolean;
    refresh: (orgName: string, groupName: string) => Promise<void>;
  };
}

const useGroupPermissions = (): IGroupPermissionsResponse => {
  const [getGroupPermissions, { data, loading, error }] = useLazyQuery(
    GET_GROUP_DATA,
    {
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((groupError): void => {
          msgError(translate.t("groupAlerts.errorTextsad"));
          Logger.warning("An error occurred group data", groupError);
        });
      },
    },
  );

  useEffect((): void => {
    if (data?.group) {
      const { group } = data;
      groupLevelPermissions.update(
        group.permissions.map((permission): { action: string } => ({
          action: permission,
        })),
      );
      groupAttributes.update(
        group.serviceAttributes.map((attribute): { action: string } => ({
          action: attribute,
        })),
      );
    }
  }, [data]);

  const refresh = useCallback(
    async (organizationName: string, groupName: string): Promise<void> => {
      await getGroupPermissions({ variables: { groupName, organizationName } });
    },
    [getGroupPermissions],
  );

  const can = useCallback(
    (permission: string): boolean => groupLevelPermissions.can(permission),
    [],
  );

  const have = useCallback(
    (attribute: string): boolean => groupAttributes.can(attribute),
    [],
  );

  const canMutate = useCallback(
    (permission: string): boolean =>
      can(`integrates_api_mutations_${permission}_mutate`),
    [can],
  );

  const canResolve = useCallback(
    (permission: string): boolean =>
      can(`integrates_api_resolvers_${permission}_resolve`),
    [can],
  );

  return {
    attributes: groupAttributes,
    error,
    loading,
    permissionMethods: {
      can,
      canMutate,
      canResolve,
      have,
      refresh,
    },
    permissions: groupLevelPermissions,
  };
};

export { useGroupPermissions };
